import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  FileEarmarkExcel,
  CalendarEvent,
  Download,
  Award,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import "../App.css";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";

const NAV_BLUE = "#183B72";
const BRAND_BLUE = "#3D74F5"; 

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  return { displayName, userId };
}

function pad2(n) {
  return String(n).padStart(2, "0");
}
function toYMD(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function reportYearLabel(year) {
  return Number(year) === 2026
    ? "1 Mar 2026 - 30 Nov 2026"
    : `1 Dec ${Number(year) - 1} - 30 Nov ${year}`;
}

function useAutoClearMessage(value, clear, delay = 5000) {
  useEffect(() => {
    if (!value) return undefined;
    const timer = setTimeout(clear, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function downloadBase64Excel(base64, filename) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, filename);
}

export default function Report() {
  const { displayName } = useMemo(getStoredUser, []);
  const navigate = useNavigate();

  const ENDPOINT_LND = `${API_BASE}/manualGenerateLnD`;
  const ENDPOINT_ANNUAL_BADGE_EXPIRY = `${API_BASE}/getAnnualBadgeExpiryEligibility`;

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [fromDate, setFromDate] = useState(toYMD(firstDayOfMonth));
  const [toDate, setToDate] = useState(toYMD(today));

  const [loading, setLoading] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const [annualReportYear, setAnnualReportYear] = useState(2026);
  const [maxReportYear, setMaxReportYear] = useState(2029);
  const reportYearOptions = Array.from(
    { length: Math.max(maxReportYear, 2026) - 2026 + 1 },
    (_, i) => 2026 + i
  );
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualOkMsg, setAnnualOkMsg] = useState("");
  const [annualErrMsg, setAnnualErrMsg] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE}/getBadgeExpiryReportYears`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
      .then((res) => {
        const max = Number(res?.data?.data?.maxYear);
        if (res?.data?.success && max >= 2026) setMaxReportYear(max);
      })
      .catch(() => {});
  }, []);

  useAutoClearMessage(okMsg, () => setOkMsg(""));
  useAutoClearMessage(errMsg, () => setErrMsg(""));
  useAutoClearMessage(annualOkMsg, () => setAnnualOkMsg(""));
  useAutoClearMessage(annualErrMsg, () => setAnnualErrMsg(""));

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => navigate(-1);

  const buildAndDownloadExcel = (rows, filename) => {
    const columns = [
      { header: "ATO", key: "ato" },
      { header: "TGLicenceNumber", key: "tg_number" },
      { header: "TypeofActivity(Learn/Do)", key: "TypeofActivity" },
      { header: "DateofActivity", key: "dateofActivity" },
      { header: "DateofSubmission", key: "dateSubmission" },
      { header: "DateofValidation", key: "dateVerified" },
      { header: "NameofLearnActivity", key: "nameActivity" },
      { header: "LearnActivityProvider", key: "learnActivityProvider" },
      { header: "TypeofLearnActivity", key: "typeActivity" },
      { header: "LearnCategory", key: "learnCategory" },
      { header: "ActivityRequirementsCompleted", key: "ActivityRequirementsCompleted" },
    ];

    const aoa = [
      columns.map((c) => c.header),
      ...rows.map((r) => columns.map((c) => (r?.[c.key] ?? ""))),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!cols"] = [
      { wch: 10 },
      { wch: 30 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 },
      { wch: 30 },
      { wch: 40 },
      { wch: 30 },
      { wch: 25 },
      { wch: 30 },
      { wch: 30 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    const arrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    downloadBlob(blob, filename);
  };

  const downloadLnDExcel = async () => {
    setOkMsg("");
    setErrMsg("");

    if (!fromDate || !toDate) {
      setErrMsg("Please select From Date and To Date.");
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      setErrMsg("From Date cannot be later than To Date.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(ENDPOINT_LND, {
        params: { fromDate, toDate },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const payload = res?.data || {};
      if (!payload.success) {
        throw new Error(payload.message || "Failed to generate report.");
      }

      const rows = Array.isArray(payload.data) ? payload.data : [];
      if (rows.length === 0) {
        throw new Error("No data found for selected date range.");
      }

      const filename = `TMIS Learn + Do Report (${fromDate} to ${toDate}).xlsx`;
      if (payload.file) {
        downloadBase64Excel(payload.file, filename);
      } else {
        buildAndDownloadExcel(rows, filename);
      }

      setOkMsg("Excel downloaded successfully.");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Server error. Please try again later.";
      setErrMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadAnnualBadgeExpiryExcel = async () => {
    setAnnualOkMsg("");
    setAnnualErrMsg("");

    const yearNum = Number(annualReportYear);
    if (!yearNum || yearNum < 2026) {
      setAnnualErrMsg("Please enter a valid report year (2026 onwards).");
      return;
    }

    setAnnualLoading(true);
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(ENDPOINT_ANNUAL_BADGE_EXPIRY, {
        params: { reportYear: yearNum },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const payload = res?.data || {};
      if (!payload.success) {
        throw new Error(payload.message || "Failed to generate report.");
      }

      const data = payload.data || {};
      const filename = `STB Annual Badge Expiry Eligibility Report (${yearNum}).xlsx`;
      downloadBase64Excel(payload.file, filename);

      setAnnualOkMsg(
        `Excel downloaded successfully. ${data.summaryRows?.length || 0} eligible guide(s) for ${reportYearLabel(yearNum)}.`
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Server error. Please try again later.";
      setAnnualErrMsg(msg);
    } finally {
      setAnnualLoading(false);
    }
  };

  return (
    <div className="custom-font" style={{ minHeight: "100vh", background: "#f7f9fc" }}>
      <Container>
        <div className="d-flex align-items-center justify-content-between py-2" style={{ gap: 16 }}>
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div className="container-xxl d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <span
                role="button"
                aria-label="Go back"
                tabIndex={0}
                onClick={handleBack}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? handleBack() : null)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  background: "transparent",
                  cursor: "pointer",
                  outline: "none",
                }}
                className="back-icon"
              >
                <ArrowLeft size={18} color="#ffffff" />
              </span>
              <h4 className="m-0 fw-bold">REPORTS</h4>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-4">
        <div className="d-flex flex-wrap align-items-stretch" style={{ gap: 20 }}>
        <Card
          style={{
            flex: "1 1 480px",
            maxWidth: 520,
            borderRadius: 16,
            border: "1px solid #e9edf5",
            boxShadow: "0 10px 26px rgba(16,24,40,0.06)",
            overflow: "hidden",
          }}
        >
          <Card.Body className="d-flex flex-column" style={{ padding: 18 }}>
            <div className="d-flex align-items-start" style={{ gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: "rgba(61,116,245,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FileEarmarkExcel size={20} color={BRAND_BLUE} />
              </div>

              <div style={{ flex: 1 }}>
                <div className="fw-bold" style={{ fontSize: 18, color: "#0f172a" }}>
                  LEARN + DO Report
                </div>
                <div style={{ fontSize: 13, color: "#1F2937", marginTop: 4 }}>
                  Generate and download LEARN + DO Excel report by date range.
                </div>
              </div>
            </div>

            <div style={{ height: 14 }} />

            {errMsg ? (
              <Alert variant="danger" className="py-2 mb-3">
                {errMsg}
              </Alert>
            ) : null}

            {okMsg ? (
              <Alert variant="success" className="py-2 mb-3">
                {okMsg}
              </Alert>
            ) : null}

            <div className="d-flex" style={{ gap: 12 }}>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="small text-muted mb-1">
                  <CalendarEvent className="me-1" />
                  From Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ borderRadius: 10 }}
                />
              </Form.Group>

              <Form.Group style={{ flex: 1 }}>
                <Form.Label className="small text-muted mb-1">
                  <CalendarEvent className="me-1" />
                  To Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ borderRadius: 10 }}
                />
              </Form.Group>
            </div>

            <div style={{ flex: 1, minHeight: 14 }} />

            <Button
              onClick={downloadLnDExcel}
              disabled={loading}
              className="w-100"
              style={{
                height: 46,
                borderRadius: 12,
                border: "none",
                background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1f57e1 100%)`,
                boxShadow: "0 10px 18px rgba(61,116,245,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Generating…
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download Excel
                </>
              )}
            </Button>
          </Card.Body>
        </Card>

        <Card
          style={{
            flex: "1 1 480px",
            maxWidth: 520,
            borderRadius: 16,
            border: "1px solid #e9edf5",
            boxShadow: "0 10px 26px rgba(16,24,40,0.06)",
            overflow: "hidden",
          }}
        >
          <Card.Body className="d-flex flex-column" style={{ padding: 18 }}>
            <div className="d-flex align-items-start" style={{ gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: "rgba(61,116,245,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Award size={20} color={BRAND_BLUE} />
              </div>

              <div style={{ flex: 1 }}>
                <div className="fw-bold" style={{ fontSize: 18, color: "#0f172a" }}>
                  Annual Compliance Report for Tourist Guides (pending internal validation)
                </div>
                <div style={{ fontSize: 13, color: "#1F2937", marginTop: 4 }}>
                  Generate the badge expiry eligibility report by report year.
                </div>
              </div>
            </div>

            <div style={{ height: 14 }} />

            {annualErrMsg ? (
              <Alert variant="danger" className="py-2 mb-3">
                {annualErrMsg}
              </Alert>
            ) : null}

            {annualOkMsg ? (
              <Alert variant="success" className="py-2 mb-3">
                {annualOkMsg}
              </Alert>
            ) : null}

            <Form.Group>
              <Form.Label className="small text-muted mb-1">
                <CalendarEvent className="me-1" />
                Report Year
              </Form.Label>
              <Form.Select
                value={annualReportYear}
                onChange={(e) => setAnnualReportYear(e.target.value)}
                style={{ borderRadius: 10 }}
              >
                {reportYearOptions.map((y) => (
                  <option key={y} value={y}>
                    {reportYearLabel(y)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div style={{ flex: 1, minHeight: 14 }} />

            <Button
              onClick={downloadAnnualBadgeExpiryExcel}
              disabled={annualLoading}
              className="w-100"
              style={{
                height: 46,
                borderRadius: 12,
                border: "none",
                background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1f57e1 100%)`,
                boxShadow: "0 10px 18px rgba(61,116,245,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {annualLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Generating…
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download Excel
                </>
              )}
            </Button>
          </Card.Body>
        </Card>
        </div>
      </Container>
    </div>
  );
}